/**
 * What sits above a thread's composer, on its column (stage 2a): a message that
 * could not be sent, the messages queued behind a reply, and the nudge to
 * compact a full thread.
 */
import { Layers, ListOrdered, Loader2, WifiOff, X } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import type { QueuedMessage } from "../api/useMessageQueue";

/**
 * A send that never reached Mohtawa. Nothing resends it on its own (the chat
 * hook has no retry queue), so it says the message was not sent and offers Try
 * again; Edit puts the words back in the box so nothing is lost; Discard drops
 * it at once, with an Undo.
 */
export function UnsentMessage({
  text,
  checked,
  onRetry,
  onEdit,
  onDiscard,
  retryDisabled,
}: {
  text: string;
  /** The thread was read and does not have it; false when that read failed too. */
  checked: boolean;
  onRetry: () => void;
  onEdit: () => void;
  onDiscard: () => void;
  retryDisabled: boolean;
}) {
  // 07 Workshop, row 45: the status banner above the box. Nothing resends on
  // its own, so it says the message is kept and offers Try now.
  return (
    <div className="ws-banner ws-banner--unsent" role="alert">
      <span className="ws-banner-icon" aria-hidden="true">
        <WifiOff strokeWidth={1.8} />
      </span>
      <div className="ws-banner-text">
        <p className="ws-banner-title">{checked ? "Can’t reach Mohtawa" : "Couldn’t confirm it was sent"}</p>
        <p className="ws-banner-meta">Your message is kept here: “{text}”</p>
        <p className="ws-banner-quiet">
          <button type="button" className="ws-text-quiet" onClick={onEdit}>
            Edit
          </button>
          <span aria-hidden="true"> · </span>
          <button type="button" className="ws-text-quiet" onClick={onDiscard}>
            Discard
          </button>
        </p>
      </div>
      <KitButton onClick={onRetry} isDisabled={retryDisabled}>
        Try now
      </KitButton>
    </div>
  );
}

/**
 * Messages written while a private reply was being written, in the order they
 * go. When the reply ends they leave together as one message. A reply that
 * was stopped, failed or ended on a card holds them: Send now sends them, and
 * the person's next message takes them with it. Edit puts one back in the box;
 * the cross takes it out, with an Undo.
 */
export function QueuedMessages({
  items,
  state,
  onSendNow,
  sendDisabled,
  onEdit,
  onRemove,
}: {
  items: QueuedMessage[];
  /** Waiting on the reply being written, on an answer to the card above, or on the person. */
  state: "reply" | "card" | "held";
  onSendNow: () => void;
  sendDisabled: boolean;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const one = items.length === 1;
  const line =
    state === "reply"
      ? one
        ? "Queued. It sends when this reply ends."
        : `${items.length} queued. They send together when this reply ends.`
      : state === "card"
        ? one
          ? "Queued. It sends once you answer above."
          : `${items.length} queued. They send together once you answer above.`
        : one
          ? "Not sent yet. It goes with your next message."
          : `${items.length} not sent yet. They go with your next message.`;
  return (
    <div className="ws-note ws-note--queued">
      <ListOrdered className="ws-note-icon" strokeWidth={1.8} aria-hidden="true" />
      <div className="ws-note-body">
        <p className="ws-note-line" role="status">
          {line}
        </p>
        <ol className="ws-queued-list">
          {items.map((item) => (
            <li key={item.id} className="ws-queued-item">
              <p className="ws-queued-text">{item.text}</p>
              <button
                type="button"
                className="ws-text-quiet"
                onClick={() => onEdit(item.id)}
                aria-label="Edit this queued message"
              >
                Edit
              </button>
              <button
                type="button"
                className="ws-queued-remove"
                onClick={() => onRemove(item.id)}
                aria-label="Remove from the queue"
                title="Remove from the queue"
              >
                <X aria-hidden="true" strokeWidth={1.8} />
              </button>
            </li>
          ))}
        </ol>
      </div>
      {state === "held" ? (
        <div className="ws-note-actions">
          <button type="button" className="ws-link" onClick={onSendNow} disabled={sendDisabled}>
            Send now
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The thread is past 60% of what the model holds. Compacting asks nothing
 * (stage 1): it summarizes older messages for the agent and keeps the whole
 * transcript on screen.
 */
export function CompactNudge({
  percent,
  compacting,
  onCompact,
}: {
  percent: number;
  compacting: boolean;
  onCompact: () => void;
}) {
  // 07 Workshop, row 25: the status banner above the box.
  return (
    <div className="ws-banner" role={compacting ? "status" : undefined}>
      <span className="ws-banner-icon" aria-hidden="true">
        {compacting ? <Loader2 className="animate-spin" strokeWidth={1.8} /> : <Layers strokeWidth={1.8} />}
      </span>
      <div className="ws-banner-text">
        <p className="ws-banner-title">
          {compacting ? "Compacting this chat" : `This chat holds ${percent}% of what Creo can keep in mind`}
        </p>
        <p className="ws-banner-meta">
          {compacting
            ? "Creo is summing up the older messages. You still see all of them."
            : "Compact sums up the older messages so Creo keeps working. You still see all of them."}
        </p>
      </div>
      {compacting ? null : <KitButton onClick={onCompact}>Compact now</KitButton>}
    </div>
  );
}
